import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import axios from "axios";
import PerfectScrollbar from "react-perfect-scrollbar";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";
import "react-h5-audio-player/lib/styles.css";

const CallDetailsTabs = ({
  callItems,
  cdnURL = "",
  emptyMessage = "No AI call records available.",
  onRefresh, // Callback to refresh call items after deletion
  leadId, // Lead ID for refreshing call details
}) => {
  const dispatch = useDispatch();
  const { adminBaseURL } = useSelector(
    (state) => state.environnment.environmentLists,
    shallowEqual
  );

  const [recallTextFind, setRecallTextFind] = useState("");
  const [deletingCallAiId, setDeletingCallAiId] = useState(null);

  // useEffect(() => {
  //   let mounted = true;
  //   (async () => {
  //     try {
  //       const { data } = await axios({
  //         method: "GET",
  //         url: `${process.env.REACT_APP_HOST}${adminBaseURL}/settings`,
  //         headers: { "Content-Type": "application/json" },
  //       });
  //       if (!mounted) return;
  //       if (data?.code === 200 && data?.data?.RECALL_TEXT_FIND) {
  //         setRecallTextFind(data.data.RECALL_TEXT_FIND);
  //       }
  //     } catch (error) {
  //       // Failed to fetch RECALL_TEXT_FIND
  //     }
  //   })();
  //   return () => {
  //     mounted = false;
  //   };
  // }, [adminBaseURL]);

  const entries = useMemo(() => {
    if (!Array.isArray(callItems)) return [];
    return callItems
      .filter(Boolean)
      .map((item, idx) => {
        const isFailureRecord =
          item &&
          typeof item.type === "string" &&
          item.type === "call_initiation_failure" &&
          String(item.call_initiation_failure || "").trim() !== "";
        return {
          item,
          displayNumber: idx + 1,
          isFailureRecord,
          key: item?._id || `call-${idx + 1}`,
        };
      });
  }, [callItems]);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [entries.length]);

  const safeActiveIndex = Math.min(activeIndex, Math.max(entries.length - 1, 0));
  const activeEntry = entries[safeActiveIndex];

  const formatTime = useCallback((ts) => {
    if (!ts) return "-";
    try {
      return new Date(ts).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return "-";
    }
  }, []);

  const formatDate = useCallback((ts) => {
    if (!ts) return "-";
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return "-";
    }
  }, []);

  const formatAudioTime = useCallback((seconds) => {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }, []);

  const getFailureLabel = useCallback((item) => {
    if (!item) return "Call Failed";

    const reason = (item.failure_reason || "").toLowerCase();
    if (reason === "busy") return "Busy Call";
    if (reason === "no-answer" || reason === "no answer") return "No Answer Call";
    if (reason === "canceled" || reason === "cancelled") return "Call Cancel";

    try {
      if (item.call_initiation_failure) {
        const parsed = JSON.parse(item.call_initiation_failure);
        const status =
          parsed?.body?.[0]?.CallStatus || parsed?.body?.[0]?.SipResponseCode;
        if (typeof status === "string") {
          const statusLc = status.toLowerCase();
          if (statusLc.includes("busy")) return "Busy Call";
          if (statusLc.includes("no-answer") || statusLc.includes("no answer"))
            return "Call Not Receive";
          if (statusLc.includes("cancel")) return "Call Cancel";
        }
      }
    } catch (err) { }

    return "Call Failed";
  }, []);

  const normalizeStatusText = useCallback((value) => {
    if (!value || typeof value !== "string") return "";
    const cleaned = value.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";
    return cleaned
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const collectFailureDescriptors = useCallback(
    (item) => {
      const parts = new Set();

      const reason = normalizeStatusText(item?.failure_reason);
      if (reason) parts.add(reason);

      const typeValue = normalizeStatusText(item?.type);
      if (typeValue) parts.add(typeValue);

      try {
        if (item?.call_initiation_failure) {
          const parsed = JSON.parse(item.call_initiation_failure);
          const body = Array.isArray(parsed?.body) ? parsed.body : [];

          body.forEach((event) => {
            const statusCandidates = [
              event?.CallStatus,
              event?.Status,
              event?.call_status,
              event?.SipResponseCode,
            ];
            statusCandidates.forEach((candidate) => {
              const normalized = normalizeStatusText(candidate);
              if (normalized) parts.add(normalized);
            });

            const eventType = normalizeStatusText(
              event?.StatusCallbackEvent || event?.EventType || event?.event_type
            );
            if (eventType) parts.add(eventType);
          });

          const message = normalizeStatusText(parsed?.message || parsed?.Message);
          if (message) parts.add(message);
        }
      } catch (err) { }

      const label = normalizeStatusText(getFailureLabel(item));
      if (label) parts.add(label);

      return Array.from(parts);
    },
    [getFailureLabel, normalizeStatusText]
  );

  const getFailureDescriptorText = useCallback(
    (item) => {
      const descriptors = collectFailureDescriptors(item);
      const ordered = [];
      const seen = new Set();

      const getCanonicalLabel = (label) => {
        if (!label) return "";
        const lower = label.toLowerCase();
        if (lower.includes("no answer") || lower.includes("not receive")) {
          return "No Answer";
        }
        if (lower.includes("busy")) {
          return "Busy";
        }
        if (lower.includes("cancel")) {
          return "Call Cancel";
        }
        if (lower.includes("fail")) {
          return "";
        }
        return label;
      };

      const pushUnique = (label) => {
        const canonical = getCanonicalLabel(label);
        if (!canonical || seen.has(canonical)) return;
        ordered.push(canonical);
        seen.add(canonical);
      };

      descriptors.forEach((descriptor) => {
        const raw = descriptor.trim();
        if (!raw) return;

        if (/^\d+$/.test(raw)) {
          // ignore pure numeric codes like 486
          return;
        }

        pushUnique(descriptor);
      });

      return ordered.join(" | ");
    },
    [collectFailureDescriptors]
  );

  const getTranscriptSummary = useCallback((item) => {
    if (!item) return "";
    const transcript = item.elevenlabs_call_transcription || item.call_transcript;
    if (!transcript) return "";
    try {
      const parsed = typeof transcript === "string" ? JSON.parse(transcript) : transcript;
      if (parsed && typeof parsed.summary === "string") {
        return parsed.summary;
      }
    } catch (err) { }
    return "";
  }, []);

  const checkAgentMessageForRecallText = useCallback(
    (item, recallText) => {
      if (!item || !recallText || !recallText.trim()) return false;

      const transcript = item.elevenlabs_call_transcription || item.call_transcript;
      if (!transcript) return false;

      try {
        const parsed = typeof transcript === "string" ? JSON.parse(transcript) : transcript;
        const transcriptRows = parsed?.transcript_rows;

        if (Array.isArray(transcriptRows) && transcriptRows.length > 0) {
          // Filter for agent messages only
          const agentMessages = transcriptRows
            .filter((row) => row?.role === "agent" && row?.message)
            .map((row) => (row.message || "").toLowerCase());

          // Check if any agent message contains the recall text
          const recallTextLower = recallText.toLowerCase().trim();
          return agentMessages.some((msg) => msg.includes(recallTextLower));
        }
      } catch (err) {
        // Error parsing transcript
      }

      return false;
    },
    []
  );

  const getSuccessDescriptor = useCallback(
    (item) => {
      if (!item) return "Call";

      // Rule 1: Check for no-answer failure
      if (
        item.type === "call_initiation_failure" &&
        (item.failure_reason === "no-answer" || item.failure_reason === "no answer")
      ) {
        return "Not Concluded";
      }

      // Rule 2: Check for voicemail detection
      if (item.voicemail_detection === true) {
        return "Not Concluded";
      }

      // Rule 3: Check elevenlabs_analysis
      if (item.elevenlabs_analysis) {
        try {
          const analysis =
            typeof item.elevenlabs_analysis === "string"
              ? JSON.parse(item.elevenlabs_analysis)
              : item.elevenlabs_analysis;

          const callScheduleEval =
            analysis?.evaluation_criteria_results?.call_schedule_evaluation?.result;
          const clientEngagementEval =
            analysis?.evaluation_criteria_results?.client_engagement_evaluation?.result;

          if (callScheduleEval && clientEngagementEval) {
            // If both evaluations have the same result
            if (callScheduleEval === clientEngagementEval) {
              return "Call Concluded";
            }
            // If call_schedule_evaluation is 'failure' and client_engagement_evaluation is 'success'
            else if (
              callScheduleEval === "failure" &&
              clientEngagementEval === "success"
            ) {
              return "Not Interested";
            } else {
              return "Not Concluded";
            }
          }
        } catch (error) {
          // Error parsing elevenlabs_analysis - continue to fallback
        }
      }

      // Fallback to other status fields
      const summary = getTranscriptSummary(item);
      if (summary) {
        // Placeholder for future summary-based labels
      }

      const message = normalizeStatusText(
        item?.elevenlabs_message || item?.status_message || item?.call_status_message
      );
      if (message) return message;

      const typeDescriptor = normalizeStatusText(item?.type || item?.callOutcome || item?.status);
      if (typeDescriptor) return typeDescriptor;

      return "Call";
    },
    [getTranscriptSummary, normalizeStatusText]
  );

  // Get Call Outcome based on status and item
  const getCallOutcome = useCallback((item, status, isFailureRecord) => {
    if (!item || !status) return "-";

    // Priority 1: Use elevenlabs_call_outcome_status from database if available
    // This field comes from the cold_leads_calling_ai table
    if (item.elevenlabs_call_outcome_status) {
      return item.elevenlabs_call_outcome_status;
    }

    // const statusLower = status.toLowerCase();

    // // Rule 1: If voicemail_detection is true, outcome is "Not Concluded"
    // if (item.voicemail_detection === true) {
    //   return "Not Concluded";
    // }

    // // Rule 2: If it's a failure record or status is from failure labels, outcome is "Not Concluded"
    if (isFailureRecord) {
      return "Not Concluded";
    }

    // const failureStatuses = [
    //   "busy call",
    //   "no answer call",
    //   "call cancel",
    //   "call not receive",
    //   "call failed",
    //   "no answer",
    //   "busy",
    //   "cancel",
    //   "cancelled"
    // ];

    // if (failureStatuses.some(failureStatus => statusLower.includes(failureStatus))) {
    //   return "Not Concluded";
    // }

    // Rule 3: If status is from getSuccessDescriptor, outcome is same as status
    // This includes: "Call Concluded", "Not Concluded", "Not Interested", or other statuses
    //`return status;
    return item.elevenlabs_call_outcome_status;
  }, []);

  const getDurationFromTranscript = useCallback((transcript) => {
    try {
      const parsed = typeof transcript === "string" ? JSON.parse(transcript) : transcript;
      const rows = parsed && parsed.transcript_rows;
      if (Array.isArray(rows) && rows.length) {
        const last = rows[rows.length - 1];
        const secs = Number(last.time_in_call_secs || 0);
        const m = Math.floor(secs / 60)
          .toString()
          .padStart(1, "0");
        const s = Math.floor(secs % 60)
          .toString()
          .padStart(2, "0");
        return `${m}:${s}`;
      }
    } catch (e) { }
    return null;
  }, []);

  const formatTranscript = useCallback((transcript) => {
    if (!transcript) return "No transcript available";

    let parsedTranscript;
    try {
      parsedTranscript =
        typeof transcript === "string" ? JSON.parse(transcript) : transcript;
    } catch (e) {
      return transcript;
    }

    if (
      parsedTranscript &&
      parsedTranscript.transcript_rows &&
      Array.isArray(parsedTranscript.transcript_rows)
    ) {
      const rows = parsedTranscript.transcript_rows;
      return (
        <div>
          {rows.map((row, index) => {
            const role = (row.role || "").toLowerCase();
            const isAgent = role === "agent";
            const label = isAgent ? "Agent" : "User";
            return (
              <div
                key={index}
                className="d-flex mb-3"
                style={{ justifyContent: isAgent ? "flex-start" : "flex-end" }}
              >
                <div
                  style={{
                    maxWidth: "85%",
                    background: isAgent ? "#e9f2ff" : "#f5f6f7",
                    border: "1px solid #e7ebf1",
                    borderRadius: 12,
                    padding: "10px 12px",
                    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                  }}
                >
                  <div
                    className="mb-1"
                    style={{ fontWeight: 600, color: isAgent ? "#0d6efd" : "#6c757d" }}
                  >
                    {label}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", color: "#101828" }}>
                    {row.message}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (Array.isArray(parsedTranscript)) {
      return parsedTranscript.map((message, index) => (
        <div key={index} className="mb-3 p-3 border rounded">
          <div className="fw-bold text-primary mb-2">
            {message.speaker || message.role || "Unknown"}
          </div>
          <div className="text-dark">
            {message.text || message.content || message.message}
          </div>
          {message.timestamp && (
            <div className="text-muted small mt-1">
              {new Date(message.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      ));
    }

    if (typeof parsedTranscript === "object") {
      return Object.entries(parsedTranscript).map(([key, value]) => (
        <div key={key} className="mb-2">
          <strong>{key}:</strong> {String(value)}
        </div>
      ));
    }

    return parsedTranscript;
  }, []);

  const handleAudioDownload = useCallback(async (fileUrl, preferredName = "call-audio.mp3") => {
    if (!fileUrl) return;
    const fileNameFromUrl = (() => {
      try {
        const segments = fileUrl.split("?")[0].split("/");
        const last = segments[segments.length - 1];
        return last || preferredName;
      } catch (error) {
        return preferredName;
      }
    })();

    try {
      const response = await fetch(fileUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileNameFromUrl || preferredName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      try {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = fileNameFromUrl || preferredName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        // Fallback audio open failed
      }
    }
  }, []);

  const {
    item: activeItem = {},
    displayNumber: activeDisplayNumber = 1,
    isFailureRecord: activeIsFailure = false,
  } = activeEntry || {};
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [tooltipTime, setTooltipTime] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, visible: false });
  const [isMuted, setIsMuted] = useState(false);
  const hasElevenLabsTranscript = Boolean(
    activeItem && activeItem.elevenlabs_call_transcription
  );
  const transcriptSource =
    activeItem &&
    (activeItem.elevenlabs_call_transcription || activeItem.call_transcript);
  const duration = getDurationFromTranscript(transcriptSource);
  const file =
    activeItem && (activeItem.elevenlabs_call_audio || activeItem.post_call_audio);
  const isAbsolute = typeof file === "string" && /^(https?:)?\/\//i.test(file);
  const base = cdnURL || "";
  const url = isAbsolute ? file : `${base}${file || ""}`;

  useEffect(() => {
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.pause();
      audioEl.load();
      setIsPlaying(false);
      setCurrentTime(0);
      setAudioDuration(0);

      const onEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      const onTimeUpdate = () => {
        if (!isDraggingRef.current) {
          setCurrentTime(audioEl.currentTime);
        }
      };
      const onLoadedMetadata = () => setAudioDuration(audioEl.duration);

      audioEl.addEventListener("ended", onEnded);
      audioEl.addEventListener("timeupdate", onTimeUpdate);
      audioEl.addEventListener("loadedmetadata", onLoadedMetadata);

      return () => {
        audioEl.removeEventListener("ended", onEnded);
        audioEl.removeEventListener("timeupdate", onTimeUpdate);
        audioEl.removeEventListener("loadedmetadata", onLoadedMetadata);
      };
    }
  }, [url]);

  const handlePlayPause = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (isPlaying) {
      audioEl.pause();
      setIsPlaying(false);
      return;
    }
    audioEl
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => setIsPlaying(false));
  };

  const handleSeekBackward = () => {
    const audioEl = audioRef.current;
    if (!audioEl || !audioDuration) return;
    const newTime = Math.max(0, currentTime - 15);
    audioEl.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleSeekForward = () => {
    const audioEl = audioRef.current;
    if (!audioEl || !audioDuration) return;
    const newTime = Math.min(audioDuration, currentTime + 15);
    audioEl.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMuteToggle = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    audioEl.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e) => {
    const audioEl = audioRef.current;
    if (!audioEl || !audioDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * audioDuration;
    audioEl.currentTime = newTime;
    setCurrentTime(newTime);
    setTooltipTime(newTime);
    setTooltipPosition({
      x: e.clientX,
      y: rect.top - 30,
      visible: true,
    });
    setTimeout(() => {
      setTooltipPosition(prev => ({ ...prev, visible: false }));
    }, 500);
  };

  const getDescriptorColor = useCallback((descriptor, isActive) => {
    if (!descriptor) return isActive ? "#ffffff" : "#6b7280";

    const descLower = descriptor.toLowerCase();

    // Success/Positive statuses
    if (descLower.includes("conclude") && !descLower.includes("not")) {
      return isActive ? "#ffffff" : "#10b981"; // Green
    }

    // Not Conclude - orange
    if (descLower.includes("not conclude")) {
      return isActive ? "#ffffff" : "#ef4444"; // Orange/Amber
    }

    // No Answer - red
    if (descLower.includes("no answer") || descLower.includes("not receive")) {
      return isActive ? "#ffffff" : "#ef4444"; // Red
    }

    // Error/Failure statuses
    if (descLower.includes("busy") || descLower.includes("cancel") || descLower.includes("fail")) {
      return isActive ? "#ffffff" : "#ef4444"; // Red
    }

    // Error/Failure statuses
    if (descLower.includes("not interested")) {
      return isActive ? "#ffffff" : "#ef4444"; // Red
    }

    // Default
    return isActive ? "#ffffff" : "#6b7280"; // Gray
  }, []);

  // Get text color for Call Status value text only (same as table: green positive #10b981, red negative #ef4444)
  // When isActive is true, return white text. When displayed as "Success" = green; other negative statuses = red.
  const getCallStatusTextColor = useCallback((status, isActive = false) => {
    if (isActive) return "#ffffff";
    if (!status) return "#111827";
    const s = status.toLowerCase();
    // Displayed as "Success" (Call Concluded, Not Interested, Not Concluded) = green (same as table)
    if (status === "Call Concluded" || status === "Not Interested" || status === "Not Concluded") return "#10b981";
    // Negative: Busy, No Answer, Cancel, Fail = red (same as table)
    if (s.includes("not conclude") || s.includes("not interested") || s.includes("no answer") || s.includes("not receive") || s.includes("busy") || s.includes("cancel") || s.includes("fail")) return "#ef4444";
    return "#10b981";
  }, []);

  // Get text color for Call Outcome value text only (same as table: green #10b981, red #ef4444)
  // When isActive is true, return white text
  const getCallOutcomeTextColor = useCallback((outcome, isActive = false) => {
    if (isActive) return "#ffffff";
    if (!outcome) return "#111827";
    const o = outcome.toLowerCase();
    // Positive first: Call Concluded, Concluded, Interested, Success (same as table)
    if ((o.includes("concluded") && !o.includes("not")) || (o.includes("interested") && !o.includes("not")) || o.includes("success")) return "#10b981";
    // Negative: Not Concluded, Not Interested, No Answer, Busy, Cancel, Fail (same as table - red #ef4444)
    if (o.includes("not concluded") || o.includes("not conclude") || o.includes("not interested") || o.includes("no answer") || o.includes("not receive") || o.includes("busy") || o.includes("cancel") || o.includes("fail")) return "#ef4444";
    return "#10b981";
  }, []);

  const handleDeleteCallAi = useCallback(async (callAiId, entry) => {
    if (!callAiId) {
      dispatch(showSuccessSnackbar("error", "Call record ID is required", 3000));
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to delete this call record? This action cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    // Set deleting state
    setDeletingCallAiId(callAiId);

    try {
      const response = await axios({
        method: "DELETE",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/deleteCallAi/${callAiId}`,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = response?.data;

      // Check for success in various response formats
      const isSuccess =
        data?.code === 200 ||
        data?.status === 200 ||
        data?.status === true ||
        (response?.status === 200 && (data?.code === undefined || data?.status === undefined));

      if (isSuccess) {
        const successMessage = data?.message || data?.data?.message || "Call record deleted successfully";

        dispatch(
          showSuccessSnackbar(
            "success",
            successMessage,
            3000
          )
        );

        // Reset active index to first item before refresh
        setActiveIndex(0);

        // If onRefresh callback is provided, call it to refresh the call items
        if (onRefresh && typeof onRefresh === "function") {
          try {
            await onRefresh();
          } catch (refreshError) {
            console.error("Error refreshing call details:", refreshError);
            // Don't show error for refresh failure, deletion was successful
          }
        }
      } else {
        const errorMessage = data?.message || data?.data?.message || "Failed to delete call record";
        dispatch(
          showSuccessSnackbar(
            "error",
            errorMessage,
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to delete call AI record", error);

      let errorMessage = "Error while deleting call record";

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const responseData = error.response.data;

        switch (status) {
          case 400:
            errorMessage = responseData?.message || "Invalid request. The call record may not exist or is invalid.";
            break;
          case 401:
            errorMessage = "Authentication required. Please log in again.";
            break;
          case 403:
            errorMessage = responseData?.message || "You do not have permission to delete this call record.";
            break;
          case 404:
            errorMessage = "The call record you are trying to delete does not exist.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = responseData?.message || responseData?.error || `Server error (${status})`;
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = "Network error. Please check your internet connection.";
      } else {
        // Error in request setup
        errorMessage = error.message || "An unexpected error occurred.";
      }

      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
    } finally {
      // Clear deleting state
      setDeletingCallAiId(null);
    }
  }, [adminBaseURL, dispatch, onRefresh]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    setIsDragging(true);
    if (progressBarRef.current && audioDuration) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const newTime = percentage * audioDuration;
      setTooltipTime(newTime);
      setTooltipPosition({
        x: e.clientX,
        y: rect.top - 30,
        visible: true,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current || !progressBarRef.current || !audioDuration) return;
      const audioEl = audioRef.current;
      if (!audioEl) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const newTime = percentage * audioDuration;
      audioEl.currentTime = newTime;
      setCurrentTime(newTime);
      setTooltipTime(newTime);
      setTooltipPosition({
        x: e.clientX,
        y: rect.top - 30,
        visible: true,
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
      setTooltipPosition(prev => ({ ...prev, visible: false }));
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, audioDuration]);

  if (!entries.length) {
    return (
      <div className="text-muted text-center">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  const perfectScrollbarOptions = {
    wheelSpeed: 2,
    wheelPropagation: false,
  };

  return (
    <div
      style={{
        border: "1px solid #e4e7ec",
        borderRadius: 12,
        background: "#ffffff",
        overflow: "hidden",
        height: "calc(100vh - 252px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="d-flex flex-wrap" style={{ background: "linear-gradient(180deg, #F3F6F9 0%, #faf8ff 100%)", flex: 1, overflow: "hidden" }}>
        {/* Left rail - call sessions */}
        <div
          style={{
            width: 280,
            borderRight: "1px solid #e5e7eb",
            background: "#f7f7f8",
            padding: "20px 16px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexShrink: 0 }}>
            <span style={{ color: "#6993FF" }}>🎧</span>
            <div style={{ fontWeight: 700, color: "#111827" }}>Call Sessions</div>
          </div>
          <PerfectScrollbar
            options={perfectScrollbarOptions}
            className="scroll"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              flex: 1,
              paddingRight: 4,
            }}
          >
            {entries.map((entry, idx) => {
              const isActive = idx === safeActiveIndex;
              const descriptor = entry.isFailureRecord
                ? getFailureLabel(entry.item)
                : getSuccessDescriptor(entry.item);
              //const outcome = getCallOutcome(entry.item, descriptor, entry.isFailureRecord);
              const outcome = getCallOutcome(entry.item, descriptor, entry.isFailureRecord);
              return (
                <div
                  key={entry.key}
                  style={{
                    position: "relative",
                    textAlign: "left",
                    border: isActive ? "1px solid #6993FF" : "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: "14px 14px",
                    background: isActive ? "#6993FF" : "#ffffff",
                    color: isActive ? "#ffffff" : "#0f172a",
                    boxShadow: isActive ? "0 8px 18px rgba(105,147,255,0.35)" : "0 2px 6px rgba(0,0,0,0.04)",
                    transition: "all 0.2s ease",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: "transparent",
                      color: "inherit",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <div style={{ fontWeight: 700 }}>
                        #{entry.displayNumber}
                      </div>
                      {entry.item?.createdOn && (
                        <div style={{ fontSize: 12, opacity: isActive ? 0.85 : 0.6, fontWeight: 400 }}>
                          {formatDate(entry.item.createdOn)} {formatTime(entry.item.createdOn)}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 13, opacity: isActive ? 0.95 : 0.75, marginBottom: 4 }}>
                      <div>
                        <strong style={{ color: isActive ? "#ffffff" : "#111827" }}>Call Status:</strong>{" "}
                        <span style={{ color: getCallStatusTextColor(descriptor, isActive) }}>
                          {(descriptor === "Call Concluded" || descriptor === "Not Interested" || descriptor === "Not Concluded") ? "Success" : (descriptor || "Call")}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, opacity: isActive ? 0.95 : 0.75 }}>
                      <div>
                        <strong style={{ color: isActive ? "#ffffff" : "#111827" }}>Call Outcome:</strong>{" "}
                        <span style={{ color: getCallOutcomeTextColor(outcome, isActive) }}>
                          {outcome || "-"}
                        </span>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (entry?.item?._id) {
                        await handleDeleteCallAi(entry.item._id, entry);
                      } else {
                        console.error("No call record ID found for deletion");
                      }
                    }}
                    disabled={deletingCallAiId === entry.item?._id}
                    style={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      /*display: "flex",*/
                      display: "none",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      border: "none",
                      background: isActive ? "rgba(255, 255, 255, 0.2)" : "transparent",
                      cursor: deletingCallAiId === entry.item?._id ? "not-allowed" : "pointer",
                      padding: 0,
                      transition: "all 0.2s ease",
                      opacity: deletingCallAiId === entry.item?._id ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (deletingCallAiId !== entry.item?._id) {
                        e.currentTarget.style.background = isActive ? "rgba(255, 255, 255, 0.3)" : "rgba(239, 68, 68, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (deletingCallAiId !== entry.item?._id) {
                        e.currentTarget.style.background = isActive ? "rgba(255, 255, 255, 0.2)" : "transparent";
                      }
                    }}
                    title={deletingCallAiId === entry.item?._id ? "Deleting..." : "Delete call"}
                  >
                    <span
                      className={isActive ? "svg-icon" : "svg-icon svg-icon-danger"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ef4444",
                      }}
                    >
                      <SVG
                        src={toAbsoluteUrl("/media/svg/icons/custom/delete.svg")}
                        height={16}
                        width={16}
                        style={{
                          fill: isActive ? "#ffffff" : "#dc2626",
                        }}
                      />
                    </span>
                  </button>
                </div>
              );
            })}
          </PerfectScrollbar>
        </div>

        {/* Main area */}
        <div style={{ flex: 1, padding: 20, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, height: "100%" }}>
          {/* Audio summary */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 12px",
              background: "#ffffff",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              marginBottom: 16,
              flexShrink: 0,
              minHeight: 52
            }}
          >
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, lineHeight: 1 }}>
                <span style={{ color: "#6993FF" }}>🎵</span>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 16 }}>{`#${activeDisplayNumber}`}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 16px", fontSize: 13, color: "#6b7280", fontWeight: 500 }}>
                <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4, lineHeight: 1, fontSize: 14 }}>
                  <i className="fa fa-calendar" style={{ fontSize: 14, color: "#6993FF" }}></i>
                  <span>{formatDate(activeItem?.createdOn)}</span>
                </span>
                <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 14 }}>
                  <i className="fa fa-clock" style={{ fontSize: 14, color: "#6993FF" }}></i>
                  <span>{duration || "—"}</span>
                </span>
                <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 14 }}>
                  <i className="fa fa-phone" style={{ fontSize: 14, transform: "rotate(90deg)", color: "#10b981" }}></i>
                  <span>{formatTime(activeItem?.createdOn)}</span>
                </span>
                <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 14 }}>
                  <i className="fa fa-phone-slash" style={{ fontSize: 14, transform: "rotate(90deg)", color: "#ef4444" }}></i>
                  <span style={{ color: "#ef4444" }}>{formatTime(activeItem?.updatedOn)}</span>
                </span>
              </div>
            </div>
            {file && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                  background: "#ffffff",
                  borderRadius: 12,
                  padding: 0,
                  marginLeft: "auto"
                }}
              >
                <audio ref={audioRef} style={{ display: "none" }}>
                  <source src={url} />
                </audio>

                {/* Seek Backward Button */}
                <button
                  type="button"
                  onClick={handleSeekBackward}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: 20,
                    padding: 0,
                    position: "relative",
                  }}
                  title="Seek backward 15 seconds"
                >
                  <i className="fa fa-undo" style={{ fontSize: 20 }}></i>
                  <span
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: 8,
                      fontWeight: 700,
                      color: "#6b7280",
                      pointerEvents: "none",
                    }}
                  >
                    15
                  </span>
                </button>

                {/* Seek Forward Button */}
                <button
                  type="button"
                  onClick={handleSeekForward}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: 20,
                    padding: 0,
                    position: "relative",
                  }}
                  title="Seek forward 15 seconds"
                >
                  <i className="fa fa-redo" style={{ fontSize: 20 }}></i>
                  <span
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: 8,
                      fontWeight: 700,
                      color: "#6b7280",
                      pointerEvents: "none",
                    }}
                  >
                    15
                  </span>
                </button>

                {/* Play/Pause Button */}
                <button
                  type="button"
                  onClick={handlePlayPause}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: "#6993FF",
                    color: "#ffffff",
                    cursor: "pointer",
                    fontSize: 14,
                    padding: 0,
                    boxShadow: "0 2px 8px rgba(105, 147, 255, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(105, 147, 255, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(105, 147, 255, 0.3)";
                  }}
                >
                  {isPlaying ? (
                    <i className="fa fa-pause" style={{ fontSize: 12, color: "#ffffff" }}></i>
                  ) : (
                    <i className="fa fa-play" style={{ fontSize: 12, marginLeft: 2, color: "#ffffff" }}></i>
                  )}
                </button>

                {/* Current Time */}
                <span
                  style={{
                    fontSize: 14,
                    color: "#111827",
                    fontWeight: 500,
                    minWidth: 38,
                    textAlign: "left",
                    display: "inline-block"
                  }}
                >
                  {formatAudioTime(currentTime)}
                </span>

                {/* Progress Bar */}
                <div
                  ref={progressBarRef}
                  onClick={handleSeek}
                  onMouseMove={(e) => {
                    if (!isDragging && progressBarRef.current && audioDuration) {
                      const rect = progressBarRef.current.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = Math.max(0, Math.min(1, x / rect.width));
                      const hoverTime = percentage * audioDuration;
                      setTooltipTime(hoverTime);
                      setTooltipPosition({
                        x: e.clientX,
                        y: rect.top - 30,
                        visible: true,
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isDragging) {
                      setTooltipPosition(prev => ({ ...prev, visible: false }));
                    }
                  }}
                  style={{
                    flex: 1,
                    height: 6,
                    background: "#e5e7eb",
                    borderRadius: 3,
                    cursor: "pointer",
                    position: "relative",
                    minWidth: 200,
                    maxWidth: 400,
                  }}
                >
                  <div
                    style={{
                      width: `${audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0}%`,
                      height: "100%",
                      background: "#6993FF",
                      borderRadius: 3,
                      transition: isDragging ? "none" : "width 0.1s linear",
                      position: "relative",
                    }}
                  >
                    <div
                      onMouseDown={handleMouseDown}
                      style={{
                        position: "absolute",
                        right: -6,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: "#ffffff",
                        border: "2px solid #6993FF",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        cursor: isDragging ? "grabbing" : "grab",
                        userSelect: "none",
                      }}
                    />
                  </div>
                  {tooltipPosition.visible && (
                    <div
                      style={{
                        position: "fixed",
                        left: tooltipPosition.x - 20,
                        top: tooltipPosition.y,
                        background: "#1f2937",
                        color: "#ffffff",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        pointerEvents: "none",
                        zIndex: 1000,
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      {formatAudioTime(tooltipTime)}
                    </div>
                  )}
                </div>

                {/* Total Duration */}
                <span
                  style={{
                    fontSize: 14,
                    color: "#111827",
                    fontWeight: 500,
                    textAlign: "right",
                    display: "inline-block"
                  }}
                >
                  {formatAudioTime(audioDuration)}
                </span>

                {/* Mute Button */}
                <button
                  type="button"
                  onClick={handleMuteToggle}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: 16,
                    padding: 0,
                  }}
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? (
                    <i className="fa fa-volume-mute" style={{ fontSize: 16 }}></i>
                  ) : (
                    <i className="fa fa-volume-up" style={{ fontSize: 16 }}></i>
                  )}
                </button>

                {/* Download Button */}
                <button
                  type="button"
                  onClick={() =>
                    handleAudioDownload(
                      url,
                      activeItem?.elevenlabs_call_audio || activeItem?.post_call_audio
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: 16,
                    padding: 0,
                  }}
                  title="Download audio"
                >
                  <i className="fa fa-download" style={{ fontSize: 16 }}></i>
                </button>
              </div>
            )}
          </div>

          {/* Body */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            {/* Transcript */}
            <div
              style={{
                borderRadius: 10,
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                padding: 16,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexShrink: 0 }}>
                <span style={{ color: "#6993FF" }}>💬</span>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Conversation Transcript</div>
              </div>
              <PerfectScrollbar
                options={perfectScrollbarOptions}
                className="scroll"
                style={{
                  flex: 1,
                  paddingRight: 6,
                  minHeight: 0,
                  maxHeight: "100%",
                }}
              >
                {transcriptSource ? (
                  <>
                    {!hasElevenLabsTranscript && (
                      <div
                        style={{
                          background: "#FEF3C7",
                          color: "#92400E",
                          borderRadius: 8,
                          padding: "10px 12px",
                          marginBottom: 12,
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        ElevenLabs call transcription not available. Showing fallback transcript.
                      </div>
                    )}
                    {formatTranscript(transcriptSource)}
                  </>
                ) : (
                  <div className="text-muted text-center">
                    <i className="fa-solid fa-file-text fa-3x mb-3"></i>
                    <p>ElevenLabs call transcription not available.</p>
                  </div>
                )}
              </PerfectScrollbar>
            </div>
          </div>

          {/* Failure state */}
          {activeIsFailure && (
            <div style={{ marginTop: 16, flexShrink: 0 }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  padding: "14px 16px",
                  borderRadius: 12,
                  border: "1px solid #fed7aa",
                  background: "#fff6ed",
                  color: "#b54708",
                  fontWeight: 600,
                  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                }}
              >
                <span>Date: {formatDate(activeItem?.createdOn)}</span>
                <span>Call Created: {formatTime(activeItem?.createdOn)}</span>
                <span>{getFailureLabel(activeItem)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallDetailsTabs;


